<template>
  <StepLayout
    :on-back="onBack"
    :show-next="false"
    :uhoh="uhoh"
    :closeUhoh="closeUhoh"
    :uhohRoute="uhohRoute"
    step-navigation-button-data-ec="bulk_send_usage_page"
  >
    <template v-slot:icon>
      <SpinLoader class="mr-sm" size="small" v-if="loading" />
    </template>
    <template v-slot:title>
      <template>
        {{ stepTitle }}
      </template>
    </template>
    <div v-if="loading">
      <TextSkeleton :pulse="true" class="block w-full h-thirtySix mb-base" />
      <TextSkeleton :pulse="true" class="block w-full h-thirtySix" />
    </div>
    <div v-else>
      <SummaryItem
        :title="$t('bulkSend.steps.usage.envelopesToSend')">
        <template v-slot:content>
          <div class="md:min-w-sixty md:text-right">
            {{ usage.envelopes_to_send }}
          </div>
        </template>
      </SummaryItem>
      <hr />
      <SummaryItem
        :title="$t('bulkSend.steps.usage.availableEnvelopes')"
      >
        <template v-slot:content>
          <div class="md:min-w-sixty md:text-right">
            {{ usage.envelopes_available_on_plan }}
          </div>
        </template>
        <template
          v-if="!usage.allowed_to_send"
          v-slot:info
        >
          <PopoverPill v-if="userHasRole('super-admin')">
            <i18n path="bulkSend.steps.usage.popoverAdmin" tag="span">
              <template v-slot:link>
                <RouterLink
                  :to="{ name: 'Billing', params: { redirectBackOnPlanSwitch: true } }"
                  target="_blank"
                  data-ec="bulk_send_usage_page"
                  data-et="go_to_billing_popover_link"
                >
                  {{ $t('bulkSend.steps.usage.popoverAdminLink') }}
                </RouterLink>
              </template>
            </i18n>
          </PopoverPill>
          <PopoverPill v-else>
            {{ $t('bulkSend.steps.usage.popoverContactAdminUpgrade') }}
          </PopoverPill>
        </template>
        <template v-slot:controls>
          <ButtonLink
            v-if="userHasRole('super-admin')"
            :to="{ name: 'Billing', params: { redirectBackOnPlanSwitch: true }}"
            data-ec="bulk_send_usage_page"
            data-et="upgrade_button"
            variant="secondary"
            size="small">
            {{ $t('companySettings.billing.upgrade') }}
          </ButtonLink>
        </template>
      </SummaryItem>
      <hr />
      <SummaryItem
        :title="$t('bulkSend.steps.usage.envelopesLeft')"
        :subtitle="$t('bulkSend.steps.usage.envelopesLeftSubtitle')">
        <template v-slot:content>
          <div class="md:min-w-sixty md:text-right">
            {{ envelopesRemaining }}
          </div>
        </template>
      </SummaryItem>
      <hr :class="{'mb-lg': isTrialling}" />
      <template v-if="!isTrialling">
        <SummaryItem
          :title="$t('bulkSend.steps.usage.overagesRequired')"
        >
          <template v-slot:content>
            <div class="md:min-w-sixty md:text-right">
              {{ usage.overages_required }}
            </div>
          </template>
          <template
            v-slot:info
            v-if="!usage.allowed_to_send"
          >
            <PopoverPill v-if="userHasRole('super-admin')">
              <i18n path="bulkSend.steps.usage.popoverAdmin" tag="span">
                <template v-slot:link>
                  <a href="#"
                    @click.prevent="openEnableOveragesModal"
                    data-ec="bulk_send_usage_page"
                    data-et="open_overages_popover_modal_link"
                  >
                    {{ $t('bulkSend.steps.usage.enableOverages') }}
                  </a>
                </template>
              </i18n>
            </PopoverPill>
            <PopoverPill v-else>
              {{ $t('bulkSend.steps.usage.popoverContactAdminOverages') }}
            </PopoverPill>
          </template>
          <template v-slot:title-extra>
            <a
              href="https://signable.help/what-happens-if-i-exceed-my-plan-limits-2/"
              target="_blank"
              :aria-label="$t('bulkSend.relatedHelpDocument')"
              class="font-normal text-placeholderGray font-body text-12 whitespace-nowrap ml-base"
              data-test="help-link"
              data-ec="bulk_send_usage_page"
              data-et="overages_help_link"
            >
              {{ $t('bulkSend.whatsThis') }}
              <ArrowUpRightFromSquareIcon class="ml-xs" />
            </a>
          </template>
          <template v-slot:controls>
            <BaseButton
              v-if="userHasRole('super-admin') && ! isTrialling"
              @click.native="openEnableOveragesModal"
              variant="secondary"
              size="small"
              data-ec="bulk_send_usage_page"
              data-et="open_overages_modal_button">
              {{ overageButtonText }}
            </BaseButton>
          </template>
        </SummaryItem>
        <hr />
        <SummaryItem
          :title="$t('bulkSend.steps.usage.overageCost')"
          :subtitle="$t('bulkSend.steps.usage.overageCostSubtitle')">
          <template v-slot:content>
            <div class="md:min-w-sixty md:text-right">
              {{ formattedOverageCost }}
            </div>
          </template>
        </SummaryItem>
        <hr class="mb-lg" />
      </template>
      <div
        v-if="usage.allowed_to_send"
        class="bg-{#f5f5f5} px-lg py-twenty rounded-10 flex flex-wrap md:flex-nowrap justify-between items-center gap-base">
        <div class="flex items-center">
          <label class="flex items-center hover:cursor-pointer" >
            <input
              type="checkbox"
              :checked="userHasAgreedToSend"
              class="mr-base h-twentyFive w-twentyFive"
              v-model="userHasAgreedToSend"
              data-ec="bulk_send_usage_page"
              data-et="agree_to_send_bulk_send_checkbox"
            />
            <i18n
              v-if="usage.overages_required"
              tag="p"
              path="bulkSend.steps.usage.userAgreementOverages" class="text-16 text-black"
            >
              <template v-slot:envelopes>
                <b>{{ $tc('bulkSend.steps.usage.envelopeCount', usage.envelopes_to_send, {count: usage.envelopes_to_send}) }}</b>
              </template>
              <template v-slot:amount>
                <b>{{ formattedOverageCost }}</b>
              </template>
              <template v-slot:planName>
                {{ plan.nickname }}
              </template>
            </i18n>
            <i18n
              v-else
              tag="p"
              path="bulkSend.steps.usage.userAgreement" class="text-16 text-black"
            >
              <template v-slot:envelopes>
                <b>{{ $tc('bulkSend.steps.usage.envelopeCount', usage.envelopes_to_send, {count: usage.envelopes_to_send}) }}</b>
              </template>
            </i18n>
          </label>
        </div>
        <BaseButton
          @click.native="sendBulkSend"
          :disabled="!userHasAgreedToSend"
          data-ec="bulk_send_usage_page"
          data-et="send_bulk_send_button"
        >
          {{ $t('bulkSend.steps.usage.confirmAndSend') }}
        </BaseButton>
      </div>
      <div
        v-else
        class="flex flex-col md:flex-row gap-base items-center justify-between px-lg py-twenty rounded-10 border-2 border-solid border-secondaryHover">
        <div class="flex items-center gap-base">
          <InfoIndicator size="xLarge" />
          <p class="text-16 text-black">{{ $t('bulkSend.steps.usage.insufficientEnvelopes') }}</p>
        </div>
        <ButtonLink
          variant="secondary"
          :to="{ name: 'Billing', params: { redirectBackOnPlanSwitch: true } }"
          data-ec="bulk_send_usage_page"
          data-et="go_to_billing_button"
        >
          {{ $t('bulkSend.steps.usage.goToBilling') }}
        </ButtonLink>
      </div>

      <ToggleOveragesModal
        :show="showOveragesModal"
        :initialOverageSetting="usage.overages_enabled"
        @close="showOveragesModal = false"
        @overageSettingUpdated="getData"
      />
    </div>
  </StepLayout>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import useRequest from '@/composables/actions/request';
import { useBulkSendStepsNavigation } from '@/composables/bulkSend/steps';
import useBulkSendUhoh from '@/composables/bulkSend/uhoh';

import BaseButton from '@/components/library/buttons/BaseButton.vue';
import InfoIndicator from '@/components/library/indicators/InfoIndicator.vue';
import ButtonLink from '@/components/library/links/ButtonLink.vue';
import SpinLoader from '@/components/library/loaders/SpinLoader.vue';
import PopoverPill from '@/components/library/pills/PopoverPill.vue';
import TextSkeleton from '@/components/library/skeletons/TextSkeleton.vue';
import StepLayout from '@/components/ui/bulkSend/StepLayout.vue';
import SummaryItem from '@/components/ui/bulkSend/SummaryItem.vue';
import ToggleOveragesModal from '@/components/ui/bulkSend/ToggleOveragesModal.vue';
import { ArrowUpRightFromSquareIcon } from '@/components/icons/fontIcons';

export default {
  name: 'UsagePage',
  components: {
    SpinLoader,
    TextSkeleton,
    PopoverPill,
    ButtonLink,
    StepLayout,
    SummaryItem,
    BaseButton,
    InfoIndicator,
    ToggleOveragesModal,
    ArrowUpRightFromSquareIcon,
  },
  setup() {
    const { onBack } = useBulkSendStepsNavigation();

    const {
      uhoh,
      closeUhoh,
      uhohRoute,
      onError,
    } = useBulkSendUhoh();

    // Send Bulk Send request
    const sendBulkSendRequest = useRequest('bulk-sends', { endpoint: 'send' });

    return {
      sendBulkSendRequest,
      onBack,
      uhoh,
      closeUhoh,
      uhohRoute,
      onError,
    };
  },
  data() {
    return {
      usage: {},
      loading: true,
      userHasAgreedToSend: false,
      showOveragesModal: false,
    };
  },
  deactivated() {
    this.userHasAgreedToSend = false;
  },
  computed: {
    ...mapGetters({
      bulkSend: 'bulkSend/bulkSend',
      userHasRole: 'roles/userHasRole',
      plan: 'plan/plan',
      isTrialling: 'plan/isTrialling',
    }),
    stepTitle() {
      if (this.loading) {
        return this.$t('bulkSend.steps.usage.titleLoading');
      }

      return this.$t('bulkSend.steps.usage.title');
    },
    formattedOverageCost() {
      return this.$n(this.usage.overages_cost / 100, 'currency', this.plan.currency);
    },
    envelopesRemaining() {
      const remaining = this.usage.envelopes_available_on_plan - this.usage.envelopes_to_send;

      return remaining > 0 ? remaining : 0;
    },
    overageButtonText() {
      return this.usage.overages_enabled ? this.$t('functional.disable') : this.$t('functional.enable');
    },
  },
  activated() {
    this.getData();
  },
  methods: {
    ...mapActions({
      fetchUsage: 'bulkSend/fetchUsage',
    }),
    async getData() {
      this.loading = true;
      try {
        this.usage = await this.fetchUsage(this.$route.params.fingerprint);
      } catch (error) {
        //
      } finally {
        this.loading = false;
      }
    },
    openEnableOveragesModal() {
      this.showOveragesModal = true;
    },
    async sendBulkSend() {
      try {
        await this.sendBulkSendRequest(
          this.$route.params.fingerprint,
          {},
          { agree_to_send_bulk_send: this.userHasAgreedToSend },
        );
      } catch (error) {
        this.onError(error);

        return;
      }

      this.$bus.$emit('renderGlobalMessage', {
        message: this.$t('bulkSend.steps.usage.sentMessage', {
          name: this.bulkSend.name,
        }),
        icon: 'check',
        type: 'success',
      });

      await this.$router.push({ name: 'BulkSends' });
    },
  },
};
</script>

